import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ListChecks } from "lucide-react";
import TopBar from "@/components/TopBar";
import StatusBadge from "@/components/StatusBadge";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { categoryLabel } from "@/lib/categories";

const FALLBACK_IMG =
  "https://images.pexels.com/photos/5872179/pexels-photo-5872179.jpeg?auto=compress&cs=tinysrgb&w=900";

export default function Wishlist() {
  const { code } = useParams();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/public/events/${code}/wishlist`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [code]);

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-24">
      <TopBar />
      <div className="max-w-5xl mx-auto px-5">
        <button
          onClick={() => navigate(`/event/${code}`)}
          data-testid="wishlist-back"
          className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("product.backToEvent")}
        </button>

        <div className="mt-6 flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#E8F6E5] text-[#3FA535] flex items-center justify-center">
            <ListChecks className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C] leading-tight">
              {t("wishlistPage.title")}
            </h1>
            <p className="mt-1 text-[#5C4664]">{t("wishlistPage.sub")}</p>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="py-16 text-center text-[#5C4664]">{t("common.loading")}</div>
          ) : items.length === 0 ? (
            <div data-testid="wishlist-empty" className="py-16 text-center text-[#5C4664]">
              {t("wishlistPage.noItems")}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {items.map((p) => (
                <Link
                  key={p.id}
                  to={`/event/${code}/shop/product/${p.id}`}
                  data-testid={`wishlist-card-${p.id}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-[#F2E2EC] hover:border-[#D81B8C]/50 hover:shadow-lg transition-all"
                >
                  <div className="aspect-square w-full bg-[#FCE7F3] overflow-hidden relative">
                    <img
                      src={p.image_url || FALLBACK_IMG}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <StatusBadge status={p.status} />
                    </div>
                  </div>
                  <div className="p-3 sm:p-4">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-[#5C4664]">
                      {categoryLabel(p.category, lang)}
                    </div>
                    <h3 className="mt-1 font-display text-xs sm:text-sm font-black text-[#2A0E3C] leading-snug line-clamp-2">
                      {p.name}
                    </h3>
                    <div className="mt-2 font-display text-base sm:text-lg font-black text-[#D81B8C]">
                      ${(p.final_price ?? p.price).toFixed(0)}
                    </div>
                    <div className="text-[9px] text-[#5C4664]">{t("common2.allInclusive")}</div>
                    {p.offering_count > 0 && (
                      <div className="mt-1 text-[10px] text-[#3FA535] font-semibold">
                        {p.offering_count} {p.offering_count > 1 ? t("product.persons") : t("product.person")} ❤
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
